import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";

export default function PalmettoHero() {
  const [address, setAddress] = useState("");

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      className="relative min-h-[100svh] pt-32 lg:pt-40 pb-16 lg:pb-24 overflow-hidden bg-[#FDFBF7]"
      data-testid="hero-section"
    >
      {/* Decorative sun */}
      <motion.div
        initial={{ opacity: 0, scale: 0.7, rotate: -20 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="absolute -right-10 lg:right-16 top-20 lg:top-28 pointer-events-none"
      >
        <svg width="260" height="260" viewBox="0 0 260 260" className="opacity-90">
          <circle cx="130" cy="130" r="52" fill="#FF9800" />
          <g stroke="#FF9800" strokeWidth="5" strokeLinecap="round">
            <line x1="130" y1="56" x2="130" y2="30" />
            <line x1="130" y1="230" x2="130" y2="204" />
            <line x1="56" y1="130" x2="30" y2="130" />
            <line x1="204" y1="130" x2="230" y2="130" />
            <line x1="80" y1="80" x2="62" y2="62" />
            <line x1="198" y1="62" x2="180" y2="80" />
            <line x1="80" y1="180" x2="62" y2="198" />
            <line x1="198" y1="198" x2="180" y2="180" />
          </g>
        </svg>
      </motion.div>

      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="eyebrow mb-6"
            >
              <span className="mr-2">HMCD</span>· Votre partenaire énergie
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif-display font-medium tracking-tight text-[#0F172A] leading-[0.95] text-[44px] sm:text-[64px] lg:text-[88px] xl:text-[100px] balance"
            >
              Votre guichet unique <br className="hidden lg:block" />
              pour <em className="not-italic text-[#2D7A4D]">économiser</em> sur l'énergie.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-8 text-[17px] lg:text-[19px] text-[#334155] max-w-xl leading-relaxed"
            >
              Panneaux photovoltaïques, isolation extérieure, batteries, pompes à chaleur —
              HMCD s'occupe de tout, de l'étude au suivi long terme.
            </motion.p>

            {/* Address CTA card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-12 lg:mt-16 bg-[#FDFBF7] border border-[#e2d9ce] rounded-2xl p-6 lg:p-8 max-w-3xl shadow-[0_1px_0_rgba(15,23,42,0.04),0_20px_50px_-30px_rgba(15,23,42,0.2)]"
              data-testid="hero-cta-card"
            >
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-4 flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2D7A4D] animate-pulse" />
                Étude gratuite · Sans engagement
              </div>

              <div className="flex flex-col lg:flex-row lg:items-end gap-5 lg:gap-6">
                <div className="flex-1">
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B] block mb-3">
                    Votre adresse
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-4 text-[#2D7A4D]" />
                    <input
                      data-testid="hero-address-input"
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="99 quai du docteur Dervaux, Asnières…"
                      className="w-full bg-transparent border-b border-[#0F172A] pb-2 pl-6 text-[15px] focus:outline-none focus:border-[#2D7A4D]"
                    />
                  </div>
                </div>

                <button
                  data-testid="hero-cta-cutit"
                  onClick={scrollToContact}
                  className="btn-primary lg:w-auto justify-center"
                >
                  Étudier mon projet
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-6 pt-5 border-t border-[#e2d9ce] flex items-center justify-between flex-wrap gap-3">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
                  Réponse sous 24 h ouvrées
                </div>
                <div className="flex items-center gap-2 text-xs text-[#2D7A4D] font-semibold">
                  RGE QualiPV · Qualibat 7141
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
